/**
 * Patterns of the owner's service-console reference (docs/design/tino-service-console-70-layouts.html)
 * that the first round of shared blocks did not have: the chip row of related pages, a stepper
 * for pipelines and workflows, a vertical timeline, progress rows, a priority task list, an
 * object hero card, label/value fact rows, a checklist of gates and a two-pane list + detail.
 * Presentational only: pages pass real data, these only lay it out.
 */

import { Check, ChevronRight, Circle, CircleAlert, CircleDot, type LucideIcon } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';
import { TONE_CLASS, type Tone } from './Badge';

/** Chips linking the pages that belong with this one (reference: "› Drop list › Yêu cầu …"). */
export function QuickLinks({ links, label, className }: { links: readonly { label: ReactNode; to: string; count?: ReactNode }[]; label?: string; className?: string }) {
  if (!links.length) return null;
  return (
    <nav aria-label={label} className={cn('flex flex-wrap gap-2', className)}>
      {links.map((l) => (
        <Link
          key={l.to}
          to={l.to}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg border bg-card px-3 text-[13px] font-semibold text-foreground shadow-xs hover:bg-muted"
        >
          <ChevronRight className="size-3.5 text-muted-foreground" aria-hidden="true" />
          {l.label}
          {l.count !== undefined ? <span className="tabular rounded bg-muted px-1.5 text-[11px] font-medium text-muted-foreground">{l.count}</span> : null}
        </Link>
      ))}
    </nav>
  );
}

export type StepState = 'done' | 'current' | 'todo' | 'blocked' | 'skipped';

export interface Step {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  state?: StepState;
  /** A count shown under the label (pipeline stages). */
  count?: ReactNode;
  to?: string;
}

const STEP_BOX: Record<StepState, string> = {
  done: 'border-success/30 bg-success-soft/40',
  current: 'border-primary/40 bg-accent text-accent-foreground',
  todo: 'bg-card',
  blocked: 'border-warning/40 bg-warning-soft/50',
  skipped: 'bg-muted/40 text-muted-foreground',
};

const STEP_ICON: Record<StepState, LucideIcon> = { done: Check, current: CircleDot, todo: Circle, blocked: CircleAlert, skipped: Circle };

/**
 * A pipeline or workflow as boxes joined by chevrons (reference: "Discover › Request › Catch ›
 * Allocate", "Winner invoice › Payment cleared › …"). Wraps to a vertical list on phones.
 */
export function Stepper({ steps, label, className }: { steps: readonly Step[]; label?: string; className?: string }) {
  return (
    <ol aria-label={label} className={cn('flex flex-col gap-2 md:flex-row md:items-stretch', className)}>
      {steps.map((s, i) => {
        const state = s.state ?? 'todo';
        const Icon = STEP_ICON[state];
        const body = (
          <>
            <span className="flex items-center justify-center gap-1.5 text-[13px] font-semibold">
              <Icon className={cn('size-3.5 shrink-0', state === 'done' ? 'text-success' : state === 'blocked' ? 'text-warning' : state === 'current' ? 'text-primary' : 'text-muted-foreground')} aria-hidden="true" />
              {s.label}
            </span>
            {s.count !== undefined ? <span className="tabular text-lg leading-tight font-semibold">{s.count}</span> : null}
            {s.hint ? <span className="text-[11px] leading-snug text-muted-foreground">{s.hint}</span> : null}
          </>
        );
        return (
          <Fragment key={s.id}>
            {i > 0 ? (
              <li aria-hidden="true" className="hidden items-center text-muted-foreground md:flex">
                <ChevronRight className="size-4" />
              </li>
            ) : null}
            <li className="min-w-0 md:flex-1" aria-current={state === 'current' ? 'step' : undefined}>
              {s.to ? (
                <Link to={s.to} className={cn('flex h-full flex-col items-center justify-center gap-1 rounded-lg border px-3 py-3 text-center hover:border-primary/50', STEP_BOX[state])}>
                  {body}
                </Link>
              ) : (
                <div className={cn('flex h-full flex-col items-center justify-center gap-1 rounded-lg border px-3 py-3 text-center', STEP_BOX[state])}>{body}</div>
              )}
            </li>
          </Fragment>
        );
      })}
    </ol>
  );
}

export interface TimelineItem {
  id: string;
  title: ReactNode;
  time?: ReactNode;
  meta?: ReactNode;
  body?: ReactNode;
  tone?: Tone;
}

const DOT: Record<Tone, string> = {
  neutral: 'bg-muted-foreground/50',
  info: 'bg-info',
  ok: 'bg-success',
  pending: 'bg-info',
  warn: 'bg-warning',
  serious: 'bg-serious',
  crit: 'bg-critical',
};

/** A vertical history: dot, title, time, who, and an optional note. Newest first is up to the caller. */
export function Timeline({ items, empty }: { items: readonly TimelineItem[]; empty?: ReactNode }) {
  if (!items.length) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <ol className="space-y-4 border-l pl-5">
      {items.map((e) => (
        <li key={e.id} className="relative space-y-0.5">
          <span className={cn('absolute top-1.5 -left-[25px] size-2.5 rounded-full ring-4 ring-card', DOT[e.tone ?? 'info'])} aria-hidden="true" />
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
            <span className="font-semibold">{e.title}</span>
            {e.time ? <span className="text-xs text-muted-foreground">{e.time}</span> : null}
          </div>
          {e.meta ? <div className="text-xs text-muted-foreground">{e.meta}</div> : null}
          {e.body ? <div className="text-sm">{e.body}</div> : null}
        </li>
      ))}
    </ol>
  );
}

const BAR: Record<Tone, string> = {
  neutral: 'bg-muted-foreground/60',
  info: 'bg-info',
  ok: 'bg-success',
  pending: 'bg-info',
  warn: 'bg-warning',
  serious: 'bg-serious',
  crit: 'bg-critical',
};

/** A labelled bar with its figure on the right (reference: "Trong lịch bắt … 42 / 284 yêu cầu"). */
export function ProgressRow({ label, value, total, right, tone = 'pending', to }: { label: ReactNode; value: number; total: number; right?: ReactNode; tone?: Tone; to?: string }) {
  const pct = total > 0 ? Math.min(100, Math.max(0, (value / total) * 100)) : 0;
  const title = to ? (
    <Link to={to} className="font-semibold hover:underline">
      {label}
    </Link>
  ) : (
    <span className="font-semibold">{label}</span>
  );
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3 text-[13px]">
        {title}
        <span className="tabular text-xs text-muted-foreground">{right ?? `${value} / ${total}`}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted" role="meter" aria-valuemin={0} aria-valuemax={total} aria-valuenow={value} aria-label={typeof label === 'string' ? label : undefined}>
        <div className={cn('h-full rounded-full', BAR[tone])} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** One line of a priority list: icon, title, hint, count on the right (reference "Công việc ưu tiên"). */
export function TaskRow({ icon: Icon, title, hint, count, tone = 'neutral', to }: { icon: LucideIcon; title: ReactNode; hint?: ReactNode; count: ReactNode; tone?: Tone; to?: string }) {
  const inner = (
    <>
      <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', TONE_CLASS[tone])}>
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold">{title}</span>
        {hint ? <span className="block text-xs text-muted-foreground">{hint}</span> : null}
      </span>
      <span className={cn('tabular text-lg font-semibold', tone === 'neutral' ? 'text-muted-foreground' : '')}>{count}</span>
    </>
  );
  return (
    <li className="border-b last:border-b-0">
      {to ? (
        <Link to={to} className="flex items-center gap-3 py-3 hover:bg-muted/40">
          {inner}
        </Link>
      ) : (
        <div className="flex items-center gap-3 py-3">{inner}</div>
      )}
    </li>
  );
}

export function TaskList({ children }: { children: ReactNode }) {
  return <ul className="-my-1">{children}</ul>;
}

/** The object hero of the reference detail pages: kicker, big name, a status badge, a subline and facts. */
export function HeroCard({ kicker, title, badge, subtitle, children, className }: { kicker?: ReactNode; title: ReactNode; badge?: ReactNode; subtitle?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-xl border bg-gradient-to-br from-accent/60 to-card p-5', className)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          {kicker ? <div className="text-[10px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">{kicker}</div> : null}
          <div className="text-2xl leading-tight font-semibold tracking-tight break-all sm:text-[28px]">{title}</div>
          {subtitle ? <div className="text-[13px] text-muted-foreground">{subtitle}</div> : null}
        </div>
        {badge ? <div className="shrink-0">{badge}</div> : null}
      </div>
      {children ? <div className="mt-4">{children}</div> : null}
    </div>
  );
}

/** Label left, value right, one per line with a rule between (reference side panels). */
export function FactRows({ rows, className }: { rows: readonly (readonly [ReactNode, ReactNode] | null | false)[]; className?: string }) {
  return (
    <dl className={cn('divide-y text-[13px]', className)}>
      {rows.filter((r): r is readonly [ReactNode, ReactNode] => Boolean(r)).map(([k, v], i) => (
        <div key={i} className="flex items-start justify-between gap-4 py-2.5 first:pt-0 last:pb-0">
          <dt className="shrink-0 text-muted-foreground">{k}</dt>
          <dd className="min-w-0 text-right break-words">{v ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A big figure with its label (hero stats). */
export function BigFigure({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0 space-y-1', className)}>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="tabular text-2xl leading-tight font-semibold tracking-tight break-words">{children}</div>
    </div>
  );
}

export interface Gate {
  id: string;
  title: ReactNode;
  hint?: ReactNode;
  /** true: met; false: not met; null: not known from the data we have. */
  ok: boolean | null;
}

/** Conditions that must hold before a step (reference "Publication gates", "Kiểm tra gate bàn giao"). */
export function GateList({ gates }: { gates: readonly Gate[] }) {
  return (
    <ul className="space-y-2">
      {gates.map((g) => (
        <li key={g.id} className="flex items-start gap-2.5 rounded-lg border px-3 py-2.5">
          <span className={cn('mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full', g.ok === true ? TONE_CLASS.ok : g.ok === false ? TONE_CLASS.warn : TONE_CLASS.neutral)}>
            {g.ok === true ? <Check className="size-3" aria-hidden="true" /> : g.ok === false ? <CircleAlert className="size-3" aria-hidden="true" /> : <Circle className="size-3" aria-hidden="true" />}
          </span>
          <span className="min-w-0">
            <span className="block text-[13px] font-semibold">{g.title}</span>
            {g.hint ? <span className="block text-xs text-muted-foreground">{g.hint}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** List on the left, the picked row's detail on the right (stacks on narrow screens). */
export function SplitPane({ list, detail, className }: { list: ReactNode; detail: ReactNode; className?: string }) {
  return (
    <div className={cn('grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_400px] [&>*]:min-w-0', className)}>
      <div className="min-w-0">{list}</div>
      <div className="min-w-0 xl:sticky xl:top-20">{detail}</div>
    </div>
  );
}

/** Main column and a side column (reference detail pages). */
export function TwoColumn({ main, side, className }: { main: ReactNode; side: ReactNode; className?: string }) {
  return (
    <div className={cn('grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_380px]', className)}>
      <div className="min-w-0 space-y-5">{main}</div>
      <div className="min-w-0 space-y-5">{side}</div>
    </div>
  );
}
